import React, { useCallback, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCopy } from "@fortawesome/free-solid-svg-icons";
import { Spinner } from "react-bootstrap";
import { LuFileUp } from "react-icons/lu";
import ConfirmationModal from "../../../components/ConfirmationModal";
import authService from "../../../utils/authService";

interface DecryptResponse {
  filename: string;
  plaintext: string;
  line_count: number;
}

function parseDecryptResponse(payload: unknown): DecryptResponse {
  let cur: unknown = payload;
  if (cur && typeof cur === "object" && "data" in cur) {
    const nested = (cur as { data: unknown }).data;
    if (nested && typeof nested === "object" && "plaintext" in nested) {
      cur = nested;
    }
  }
  const obj =
    cur && typeof cur === "object" ? (cur as Record<string, unknown>) : {};
  const plaintext = obj.plaintext ?? obj.text ?? obj.content ?? "";
  return {
    filename: obj.filename != null ? String(obj.filename) : "",
    plaintext: plaintext != null ? String(plaintext) : "",
    line_count: Number(obj.line_count || 0),
  };
}

function readApiErrorDetail(err: unknown): string {
  const detail =
    err &&
    typeof err === "object" &&
    "response" in err &&
    err.response &&
    typeof err.response === "object" &&
    "data" in err.response &&
    err.response.data &&
    typeof err.response.data === "object" &&
    "detail" in err.response.data
      ? (err.response.data as { detail: unknown }).detail
      : null;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) =>
        item && typeof item === "object" && "msg" in item
          ? String((item as { msg: unknown }).msg)
          : "",
      )
      .filter(Boolean);
    if (messages.length) return messages.join("; ");
  }
  return "Could not decrypt this file.";
}

function pickEncFile(list: FileList | File[] | null | undefined): File | null {
  if (!list || list.length === 0) return null;
  const files = Array.from(list);
  return files.find((file) => file.name.toLowerCase().endsWith(".enc")) || files[0];
}

function fileFromDataTransfer(data: DataTransfer | null): File | null {
  if (!data) return null;
  const fromFiles = pickEncFile(data.files);
  if (fromFiles) return fromFiles;
  const items = data.items;
  if (!items) return null;
  for (let i = 0; i < items.length; i += 1) {
    if (items[i].kind !== "file") continue;
    const file = items[i].getAsFile();
    if (file) return file;
  }
  return null;
}

const DesktopDiagnosticsLogs: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [plaintext, setPlaintext] = useState("");
  const [lineCount, setLineCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [enlarged, setEnlarged] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleUpload = useCallback(async (file: File) => {
    setError("");
    setPlaintext("");
    setLineCount(0);
    setFileName(file.name);
    setLoading(true);
    try {
      const body = new FormData();
      body.append("file", file, file.name);
      const response = await authService.post(
        "admin/desktop-diagnostics/decrypt",
        body,
      );
      const data = parseDecryptResponse(response.data);
      if (!data.plaintext) {
        throw new Error("Decrypt succeeded but the log text was empty.");
      }
      setPlaintext(data.plaintext);
      setLineCount(data.line_count || data.plaintext.split("\n").length);
      if (data.filename) setFileName(data.filename);
    } catch (err: unknown) {
      setPlaintext("");
      setLineCount(0);
      setError(
        err instanceof Error && !("response" in err)
          ? err.message
          : readApiErrorDetail(err),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = pickEncFile(event.target.files);
    event.target.value = "";
    if (file) void handleUpload(file);
  };

  const onDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setDragOver(true);
  };

  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node)) return;
    setDragOver(false);
  };

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    const file = fileFromDataTransfer(event.dataTransfer);
    if (file) {
      void handleUpload(file);
      return;
    }
    setError("Drop an encrypted .enc file.");
  };

  const copyPlaintext = useCallback(async () => {
    if (!plaintext) return;
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setError("Could not copy the decrypted log.");
    }
  }, [plaintext]);

  const copyButton = plaintext ? (
    <button
      type="button"
      className="btn btn-dark rounded-circle shadow-sm d-flex align-items-center justify-content-center"
      style={{
        position: "absolute",
        top: 12,
        right: 12,
        zIndex: 2,
        width: 36,
        height: 36,
        padding: 0,
      }}
      title={copied ? "Copied" : "Copy"}
      aria-label={copied ? "Copied" : "Copy decrypted log"}
      onClick={() => void copyPlaintext()}
    >
      <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
    </button>
  ) : null;

  const logView = (
    <pre
      className="mb-0 p-3 bg-dark text-light"
      style={{
        fontSize: "12px",
        lineHeight: 1.45,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        minHeight: "240px",
        paddingRight: plaintext ? 52 : undefined,
      }}
    >
      {plaintext || "Drag and drop an encrypted .enc file to decrypt it."}
    </pre>
  );

  return (
    <div
      className="border rounded-2 bg-white my-2 me-2 d-flex flex-column"
      style={{ height: "calc(100vh - 88px)", overflow: "hidden" }}
    >
      <div className="flex-grow-1 overflow-auto p-3 d-flex flex-column">
        <input
          ref={inputRef}
          type="file"
          accept=".enc,application/octet-stream"
          className="d-none"
          onChange={onFileChange}
        />

        <div
          className="border rounded-2 mb-3 p-3 text-center"
          role="button"
          tabIndex={0}
          onClick={() => {
            if (!loading) inputRef.current?.click();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              if (!loading) inputRef.current?.click();
            }
          }}
          onDragOver={onDragOver}
          onDragEnter={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          style={{
            cursor: loading ? "wait" : "pointer",
            borderStyle: "dashed",
            background: dragOver ? "#eef5ff" : "#f8f9fa",
            borderColor: dragOver ? "#0d6efd" : undefined,
            flexShrink: 0,
          }}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="mb-2" />
              <div className="fw-semibold">Decrypting…</div>
            </>
          ) : (
            <>
              <LuFileUp size={22} className="mb-1 text-primary" />
              <div className="fw-semibold">
                {dragOver
                  ? "Drop the file to decrypt"
                  : "Drag and drop the .enc file"}
              </div>
              <div className="small text-muted">or click to browse</div>
            </>
          )}
        </div>

        {error ? <div className="alert alert-danger py-2">{error}</div> : null}

        <div
          className="border rounded-2 flex-grow-1 position-relative"
          style={{ minHeight: 280 }}
        >
          {copyButton}
          <div className="h-100 overflow-auto" style={{ minHeight: 280 }}>
            {logView}
          </div>
        </div>
      </div>

      <ConfirmationModal
        show={enlarged}
        onHide={() => setEnlarged(false)}
        onConfirm={() => setEnlarged(false)}
        title={
          fileName
            ? `${fileName}${lineCount ? ` · ${lineCount} lines` : ""}`
            : "Diagnostics log"
        }
        confirmText="Close"
        cancelText=""
        size="xl"
        dialogClassName="desktop-diagnostics-enlarge-modal"
      >
        <div
          className="border rounded-2 desktop-diagnostics-enlarge-body position-relative"
          style={{ height: "70vh" }}
        >
          {copyButton}
          <div className="h-100 overflow-auto">
            {logView}
          </div>
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default DesktopDiagnosticsLogs;
